<template>
  <div class="pgm-dashboard">
    <!-- 로딩 컴포넌트 -->
    <LodingComponent :is-visible="isLoading" :text="loadingText" />
    
    <!-- 대시보드 헤더 -->
    <div class="dashboard-header">
      <div class="header-left">
        <h1 class="dashboard-title">
          <template v-if="store.state.user?.m_sisul_name">
            {{ store.state.user.m_sisul_name }}
          </template>
          <router-link 
            v-else 
            to="/pgm/userinfo" 
            class="facility-name-link"
          >
            기관명을 입력해주세요
          </router-link>
        </h1>
        <p class="dashboard-subtitle">{{ selectedYear }}년 기관 운영 현황을 한눈에 확인하세요.</p>
      </div>
      <div class="header-right">
        <v-select
          v-model="selectedYear"
          :items="availableYears"
          item-title="text"
          item-value="value"
          variant="outlined"
          density="compact"
          hide-details
          class="year-select-header"
          prepend-inner-icon="mdi-calendar"
          @update:model-value="onYearChange"
        ></v-select>
      </div>
    </div>

    <!-- 임금관리 현황 섹션 -->
    <div class="wage-process-section">
      <WageProcessStatus 
        :selected-year="selectedYear"
      />
    </div>

    <!-- KPI 카드 섹션 -->
    <div class="kpi-grid">
      <!-- 총 인건비 -->
      <div class="kpi-card kpi-card-modern primary-card">
        <div class="kpi-card-content">
          <div class="kpi-top-section">
            <div class="kpi-label-wrapper">
              <span class="kpi-label">총 인건비</span>
              <span class="kpi-subtitle">{{ selectedYear }}년 누적</span>
            </div>
            <div class="kpi-icon-wrapper primary">
              <v-icon color="white" size="32">mdi-cash-multiple</v-icon>
            </div>
          </div>
          <div class="kpi-value-section">
            <h2 class="kpi-value">{{ formatCurrency(getTotalWage()) }}</h2>
            <div class="kpi-trend-wrapper">
            </div>
            <div class="kpi-latest-month" v-if="getLatestMonthData()">
              <div class="latest-badge">
                <v-icon size="14" class="badge-icon">mdi-calendar-month</v-icon>
                <span class="badge-month">{{ getLatestMonthData()?.month }}월</span>
              </div>
              <span class="latest-amount">{{ formatCurrency(getLatestMonthData()?.wage || 0) }}</span>
            </div>
          </div>
        </div>
        <div class="kpi-card-accent primary"></div>
      </div>

      <!-- 센터 수익 -->
      <div class="kpi-card kpi-card-modern success-card">
        <div class="kpi-card-content">
          <div class="kpi-top-section">
            <div class="kpi-label-wrapper">
              <span class="kpi-label">센터 수익</span>
              <span class="kpi-subtitle">{{ selectedYear }}년 누적</span>
            </div>
            <div class="kpi-icon-wrapper success">
              <v-icon color="white" size="32">mdi-chart-line</v-icon>
            </div>
          </div>
          <div class="kpi-value-section">
            <h2 class="kpi-value">{{ formatCurrency(getTotalProfit()) }}</h2>
            <div class="kpi-trend-wrapper">
            </div>
            <div class="kpi-latest-month" v-if="getLatestMonthData()">
              <div class="latest-badge">
                <v-icon size="14" class="badge-icon">mdi-calendar-month</v-icon>
                <span class="badge-month">{{ getLatestMonthData()?.month }}월</span>
              </div>
              <span class="latest-amount">{{ formatCurrency(getLatestMonthData()?.profit || 0) }}</span>
            </div>
          </div>
        </div>
        <div class="kpi-card-accent success"></div>
      </div>

      <!-- 직원 수 -->
      <div class="kpi-card kpi-card-modern info-card">
        <div class="kpi-card-content">
          <div class="kpi-top-section">
            <div class="kpi-label-wrapper">
              <span class="kpi-label">평균 직원 수</span>
              <span class="kpi-subtitle">월평균 인원</span>
            </div>
            <div class="kpi-icon-wrapper info">
              <v-icon color="white" size="32">mdi-account-group</v-icon>
            </div>
          </div>
          <div class="kpi-value-section">
            <h2 class="kpi-value">{{ getAverageEmployeeCount() }}<span class="value-unit">명</span></h2>
            <div class="kpi-trend-wrapper">
            </div>
            <div class="kpi-latest-month" v-if="getLatestMonthData()">
              <div class="latest-badge">
                <v-icon size="14" class="badge-icon">mdi-calendar-month</v-icon>
                <span class="badge-month">{{ getLatestMonthData()?.month }}월</span>
              </div>
              <span class="latest-amount">{{ getLatestMonthData()?.count || 0 }}명</span>
            </div>
          </div>
        </div>
        <div class="kpi-card-accent info"></div>
      </div>

      <!-- 절감액 -->
      <div class="kpi-card kpi-card-modern warning-card">
        <div class="kpi-card-content">
          <div class="kpi-top-section">
            <div class="kpi-label-wrapper">
              <span class="kpi-label">총 절감액</span>
              <span class="kpi-subtitle">{{ selectedYear }}년 누적</span>
            </div>
            <div class="kpi-icon-wrapper warning">
              <v-icon color="white" size="32">mdi-piggy-bank</v-icon>
            </div>
          </div>
          <div class="kpi-value-section">
            <h2 class="kpi-value">{{ formatCurrencyWithThousands(getTotalSavings()) }}</h2>
            <div class="kpi-trend-wrapper">
            </div>
            <div class="kpi-latest-month" v-if="getLatestMonthData()">
              <div class="latest-badge">
                <v-icon size="14" class="badge-icon">mdi-calendar-month</v-icon>
                <span class="badge-month">{{ getLatestMonthData()?.month }}월</span>
              </div>
              <span class="latest-amount">{{ formatCurrencyWithThousands(getLatestMonthData()?.savings || 0) }}</span>
            </div>
          </div>
        </div>
        <div class="kpi-card-accent warning"></div>
      </div>
    </div>



    <!-- 차트 섹션 -->
    <div class="charts-section">
      <v-card class="charts-container-card" elevation="0">
        <v-card-title class="charts-header">
          <div class="header-content">
            <div class="header-left-content">
              <v-icon color="#1e3a8a" class="header-icon">mdi-chart-box-multiple</v-icon>
              <div>
                <h2 class="charts-title">{{ selectedYear }}년 통계 분석</h2>
                <p class="charts-subtitle">월별 데이터 추이 및 비교 분석</p>
              </div>
            </div>
          </div>
        </v-card-title>
        <v-card-text class="charts-content">
          <GraphChart :chart-data="wageMagamData" />
        </v-card-text>
      </v-card>
    </div>

    <!-- 공지사항 섹션 -->
    <div class="news-section">
      <NewsList />
    </div>

    <!-- 사원 정보 업로드 모달 -->
    <MemberUploadModal 
      v-model="showMemberUploadDialog"
      @uploaded="handleMemberUploaded"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';
import LodingComponent from '@/components/loading/LodingComponent.vue';
import NewsList from '@/components/dashboard/NewsList.vue';
import GraphChart from '@/components/dashboard/GraphChart.vue';
import WageProcessStatus from '@/components/dashboard/WageProcessStatus.vue';
import MemberUploadModal from '@/components/employee/MemberUploadModal.vue';

const store = useStore();
const router = useRouter();

// 로딩 상태
const isLoading = ref(false);
const loadingText = ref('로딩 중...');

// 연도 선택
const currentYear = new Date().getFullYear();
const selectedYear = ref(currentYear);
const availableYears = ref(
  Array.from({ length: 11 }, (_, i) => {
    const year = currentYear - 5 + i;
    return { value: year, text: `${year}년` };
  })
);

// 급여 마감 데이터
const wageMagamData = ref<any[]>([]);

// 요양보호사 정보
const showMemberUploadDialog = ref(false);

// 스낵바 표시
const showSnackbar = (text: string, color: string = 'success') => {
  const timeout = color === 'error' && text.length > 100 ? 8000 : 4000;
  store.commit('showSnackbar', { text, color, timeout });
};

// 연도 변경 핸들러
const onYearChange = async (newYear: number) => {
  selectedYear.value = newYear;
  await fetchYearlyWageMagamData(newYear);
};

// 급여 마감 데이터 조회
const fetchYearlyWageMagamData = async (year: number) => {
  try {
    const user = store.state.user;
    if (!user || !user.m_sisul_code) {
      console.warn('사용자 정보가 없습니다.');
      return;
    }

    isLoading.value = true;
    loadingText.value = '급여 데이터를 불러오는 중...';

    const response = await axios.get(`/api/calc/wage-magam-yearly/${user.m_sisul_code}/${year}`, {
      withCredentials: true
    });

    if (response.data.success) {
      wageMagamData.value = response.data.data;
    } else {
      console.error('급여 마감 데이터 조회 실패:', response.data.message);
      wageMagamData.value = [];
    }
  } catch (error) {
    console.error('급여 마감 데이터 조회 오류:', error);
    wageMagamData.value = [];
  } finally {
    isLoading.value = false;
  }
};

// 요양보호사 정보 모달 열기 (사용되지 않지만 UI에서 접근 가능하도록 유지)
const openMemberUploadDialog = () => {
  showMemberUploadDialog.value = true;
};

// 사원 정보 업로드 완료 핸들러
const handleMemberUploaded = () => {
  // 업로드 완료 후 필요한 처리
};

// 가장 최근 마감된 월 찾기
const getLatestMagamMonth = (): { month: number; yearMonth: string } | null => {
  if (!wageMagamData.value || wageMagamData.value.length === 0) return null;
  
  const totalData = wageMagamData.value.filter(item => item.wm_job_type === '전체합계');
  if (totalData.length === 0) return null;
  
  // wm_ym 기준으로 정렬 (내림차순)
  const sortedData = [...totalData].sort((a, b) => {
    const aYm = parseInt(a.wm_ym || '0');
    const bYm = parseInt(b.wm_ym || '0');
    return bYm - aYm;
  });
  
  if (sortedData.length === 0) return null;
  
  const latest = sortedData[0];
  const yearMonth = latest.wm_ym || '';
  const month = parseInt(yearMonth.substring(4, 6));
  
  return { month, yearMonth };
};

// 최근 한 달 데이터 가져오기
const getLatestMonthData = () => {
  const latest = getLatestMagamMonth();
  if (!latest) return null;
  
  const latestData = wageMagamData.value.find(
    item => item.wm_job_type === '전체합계' && item.wm_ym === latest.yearMonth
  );
  
  if (!latestData) return null;
  
  return {
    month: latest.month,
    wage: parseInt(latestData.wm_wage_sum) || 0,
    profit: parseInt(latestData.wm_center_profit) || 0,
    savings: parseInt(latestData.wm_center_savings) || 0,
    count: parseInt(latestData.wm_count) || 0
  };
};

// KPI 계산 함수들
const getTotalWage = (): number => {
  if (!wageMagamData.value || wageMagamData.value.length === 0) return 0;
  
  return wageMagamData.value
    .filter(item => item.wm_job_type === '전체합계')
    .reduce((sum, item) => sum + (parseInt(item.wm_wage_sum) || 0), 0);
};

const getTotalProfit = (): number => {
  if (!wageMagamData.value || wageMagamData.value.length === 0) return 0;
  
  return wageMagamData.value
    .filter(item => item.wm_job_type === '전체합계')
    .reduce((sum, item) => sum + (parseInt(item.wm_center_profit) || 0), 0);
};

const getAverageEmployeeCount = (): number => {
  if (!wageMagamData.value || wageMagamData.value.length === 0) return 0;
  
  const totalData = wageMagamData.value.filter(item => item.wm_job_type === '전체합계');
  if (totalData.length === 0) return 0;
  
  const totalCount = totalData.reduce((sum, item) => sum + (parseInt(item.wm_count) || 0), 0);
  return Math.round(totalCount / totalData.length);
};

const getTotalSavings = (): number => {
  if (!wageMagamData.value || wageMagamData.value.length === 0) return 0;
  
  return wageMagamData.value
    .filter(item => item.wm_job_type === '전체합계')
    .reduce((sum, item) => {
      const centerSavings = parseInt(item.wm_center_savings) || 0;
      return sum + centerSavings;
    }, 0);
};

const formatCurrency = (value: number): string => {
  if (value >= 10000) {
    const tenThousands = Math.floor(value / 10000);
    return `${tenThousands.toLocaleString()}만`;
  }
  return value.toLocaleString();
};

// 천원 단위까지 표시하는 포맷 함수 (총 절감액용)
const formatCurrencyWithThousands = (value: number): string => {
  if (value >= 10000) {
    const tenThousands = Math.floor(value / 10000);
    const thousands = Math.floor((value % 10000) / 1000);
    if (thousands > 0) {
      return `${tenThousands.toLocaleString()}만${thousands}천`;
    }
    return `${tenThousands.toLocaleString()}만`;
  }
  return value.toLocaleString();
};

// 컴포넌트 마운트 시
onMounted(async () => {
  await store.dispatch('validateAuth');
  await fetchYearlyWageMagamData(selectedYear.value);
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/pgmDashboard.scss';

.pgm-dashboard {
  padding: 24px;
  max-width: 1600px;
  margin: 0 auto;
  background: #ffffff;
  min-height: 100vh;
}

// 대시보드 헤더
.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
  padding: 0 4px;
  
  .header-left {
    flex: 1;
    
    .dashboard-title {
      font-size: 1.75rem;
      font-weight: 800;
      color: #1a202c;
      margin: 0 0 6px 0;
      letter-spacing: -0.8px;
      color: #1e3a8a;
      
      .facility-name-link {
        color: #1e3a8a;
        text-decoration: none;
        transition: all 0.2s ease;
        border-bottom: 2px solid transparent;
        
        &:hover {
          color: #2563eb;
          border-bottom-color: #2563eb;
        }
      }
    }
    
    .dashboard-subtitle {
      font-size: 0.9rem;
      color: #6c757d;
      margin: 0;
      font-weight: 500;
      letter-spacing: -0.2px;
    }
  }
  
  .header-right {
    .year-select-header {
      min-width: 160px;
      
      :deep(.v-field) {
        background: white;
        border-radius: 10px;
        border: 1px solid #e0e0e0;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        transition: all 0.2s ease;
        
        &:hover {
          border-color: #667eea;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
        }
      }
      
      :deep(.v-field__input) {
        font-weight: 600;
        color: #2c3e50;
        font-size: 0.95rem;
      }
    }
  }
}

// KPI 그리드
.kpi-grid {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.kpi-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #e0e0e0;
  position: relative;
  
  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);
    border-color: transparent;
  }
  
  &.kpi-card-modern {
    .kpi-card-content {
      padding: 16px;
      position: relative;
      z-index: 1;
    }
    
    .kpi-card-accent {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 4px;
      
      &.primary {
        background: #1e3a8a;
      }
      
      &.success {
        background: #1e3a8a;
      }
      
      &.info {
        background: #1e3a8a;
      }
      
      &.warning {
        background: #1e3a8a;
      }
    }
  }
  
  &.primary-card .kpi-icon-wrapper {
    background: #1e3a8a;
    box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
  }
  
  &.success-card .kpi-icon-wrapper {
    background: #1e3a8a;
    box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
  }
  
  &.info-card .kpi-icon-wrapper {
    background: #1e3a8a;
    box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
  }
  
  &.warning-card .kpi-icon-wrapper {
    background: #1e3a8a;
    box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
  }
  
  .kpi-top-section {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
    
    .kpi-label-wrapper {
      display: flex;
      flex-direction: column;
      gap: 2px;
      
      .kpi-label {
        font-size: 1rem;
        color: #000000;
        font-weight: 600;
        letter-spacing: -0.2px;
      }
      
      .kpi-subtitle {
        font-size: 0.7rem;
        color: #9e9e9e;
        font-weight: 400;
      }
    }
    
    .kpi-icon-wrapper {
      width: 52px;
      height: 52px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
  }
  
  .kpi-value-section {
    .kpi-value {
      font-size: 1.8rem;
      font-weight: 700;
      color: #2c3e50;
      line-height: 1.2;
      margin: 0 0 0 0;
      letter-spacing: -0.5px;
      
      .value-unit {
        font-size: 1.2rem;
        font-weight: 600;
        color: #6c757d;
        margin-left: 2px;
      }
    }
    
    .kpi-trend-wrapper {
      .kpi-trend {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 0.75rem;
        font-weight: 600;
        padding: 4px 8px;
        border-radius: 6px;
        
        &.success {
          color: #1e3a8a;
          background: rgba(30, 58, 138, 0.1);
        }
        
        &.info {
          color: #1e3a8a;
          background: rgba(30, 58, 138, 0.1);
        }
        
        &.warning {
          color: #1e3a8a;
          background: rgba(30, 58, 138, 0.1);
        }
        
        &.danger {
          color: #1e3a8a;
          background: rgba(30, 58, 138, 0.1);
        }
      }
    }

    .kpi-latest-month {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-top: 10px;
      padding: 8px 12px;
      background: #f8f9fa;
      border-left: 3px solid #dee2e6;
      border-radius: 4px;

      .latest-badge {
        display: flex;
        align-items: center;
        gap: 4px;

        .badge-icon {
          color: #6c757d;
        }

        .badge-month {
          color: #495057;
          font-size: 0.8rem;
          font-weight: 600;
        }
      }

      .latest-amount {
        color: #212529;
        font-size: 1rem;
        font-weight: 700;
      }
    }
  }
}

// 파일 제출 현황 섹션
.file-upload-section {
  margin-bottom: 24px;
}

// 임금관리 현황 섹션
.wage-process-section {
  margin-bottom: 24px;
}

// 공지사항 섹션
.news-section {
  margin-bottom: 24px;
}

// 차트 섹션
.charts-section {
  margin-bottom: 24px;
}

.charts-container-card {
  border-radius: 12px;
  background: white;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }
}

.charts-header {
  padding: 12px 16px;
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;

  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    
    .header-left-content {
      display: flex;
      align-items: center;
      gap: 10px;
      
      .header-icon {
        font-size: 24px;
      }
      
      .charts-title {
        font-size: 1.25rem;
        font-weight: 700;
        margin: 0 0 1px 0;
        color: #1f2937;
      }
      
      .charts-subtitle {
        font-size: 0.85rem;
        color: #6b7280;
        margin: 0;
        font-weight: 400;
      }
    }
  }
}

.charts-content {
  padding: 16px;
  background: white;
}

// 반응형 디자인
@media (max-width: 1024px) {
  .pgm-dashboard {
    padding: 20px 16px;
  }
  
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  .content-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .pgm-dashboard {
    padding: 16px 12px;
  }

  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 20px;
    
    .header-left {
      .dashboard-title {
        font-size: 1.5rem;
      }
      
      .dashboard-subtitle {
        font-size: 0.85rem;
      }
    }
    
    .header-right {
      width: 100%;
      
      .year-select-header {
        width: 100%;
      }
    }
  }

  .kpi-grid {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 20px;
  }

  .kpi-card {
    &.kpi-card-modern {
      .kpi-card-content {
        padding: 18px;
      }
    }
    
    .kpi-top-section {
      .kpi-icon-wrapper {
        width: 48px;
        height: 48px;
      }
    }
    
    .kpi-value-section {
      .kpi-value {
        font-size: 1.6rem;
      }
    }
  }

  .content-grid {
    gap: 16px;
    margin-bottom: 20px;
  }

  .charts-header {
    padding: 16px 20px;

    .header-content {
      .header-left-content {
        gap: 10px;
        
        .header-icon {
          font-size: 24px;
        }

        .charts-title {
          font-size: 1rem;
        }
        
        .charts-subtitle {
          font-size: 0.72rem;
        }
      }
    }
  }

  .charts-content {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .pgm-dashboard {
    padding: 12px 8px;
  }
  
  .dashboard-header {
    margin-bottom: 16px;
    
    .header-left {
      .dashboard-title {
        font-size: 1.35rem;
      }
      
      .dashboard-subtitle {
        font-size: 0.8rem;
      }
    }
  }
  
  .kpi-grid {
    gap: 12px;
    margin-bottom: 16px;
  }
  
  .kpi-card {
    &.kpi-card-modern {
      .kpi-card-content {
        padding: 16px;
      }
    }
    
    .kpi-top-section {
      margin-bottom: 12px;
      
      .kpi-icon-wrapper {
        width: 44px;
        height: 44px;
      }
      
      .kpi-label-wrapper {
        .kpi-label {
          font-size: 0.75rem;
        }
        
        .kpi-subtitle {
          font-size: 0.65rem;
        }
      }
    }
    
    .kpi-value-section {
      .kpi-value {
        font-size: 1.5rem;
      }
    }
  }
  
  .content-grid {
    gap: 12px;
    margin-bottom: 16px;
  }
  
  .charts-section {
    margin-bottom: 16px;
  }
  
  .charts-header {
    padding: 14px 16px;
    
    .header-content {
      .header-left-content {
        .charts-title {
          font-size: 0.95rem;
        }
      }
    }
  }
  
  .charts-content {
    padding: 12px;
  }
}
</style>